import { AlertCircle, CheckCheck } from 'lucide-react';
import { useNotifications, useMarkAsRead, useMarkAllAsRead } from '../../hooks/useNotifications';
import { NotificationItem } from './NotificationItem';

interface NotificationDropdownProps {
  onClose: () => void;
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  // useNotifications() resolves the {notifications, total} envelope the backend
  // actually sends. Reading `data` as an array threw `.map is not a function`
  // during render (djin-cloud#739). `total` is the unpaginated count — unused
  // today, kept on the wire for the pagination the route already implements.
  const { data, isLoading, isError } = useNotifications();
  const notifications = data?.notifications ?? [];
  const markRead = useMarkAsRead();
  const markAllRead = useMarkAllAsRead();

  const handleMarkRead = (id: string) => {
    markRead.mutate(id);
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate();
  };

  return (
    <div
      className="absolute right-0 top-full mt-1 w-80 bg-ems-bg-card rounded-xl border border-ems-border z-50 overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ems-border">
        <h3 className="text-sm font-semibold text-ems-text-primary">Notifications</h3>
        <button
          onClick={handleMarkAllRead}
          disabled={markAllRead.isPending}
          className="flex items-center gap-1 text-xs text-ems-green hover:text-ems-green/80 transition-colors disabled:opacity-50"
        >
          <CheckCheck className="w-3.5 h-3.5" />
          Mark all read
        </button>
      </div>

      {/* List */}
      <div className="max-h-80 overflow-y-auto divide-y divide-ems-border">
        {isLoading ? (
          <div className="px-4 py-8 text-center">
            <p className="text-xs text-ems-text-muted">Loading notifications...</p>
          </div>
        ) : isError ? (
          // A failed request must NOT read as an empty inbox. Without this branch
          // `data?.notifications ?? []` collapses "we could not ask" into "nothing
          // to see" — the same silent-blank failure class as djin-cloud#739 itself,
          // and react-query retries first, so the user watches a spinner resolve
          // into a confident wrong answer.
          <div className="px-4 py-8 text-center">
            <AlertCircle className="w-5 h-5 text-ems-red mx-auto mb-1.5" />
            <p role="alert" className="text-xs text-ems-text-muted">Couldn't load notifications</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-xs text-ems-text-muted">No notifications</p>
          </div>
        ) : (
          notifications.map((n) => (
            <NotificationItem key={n.id} notification={n} onMarkRead={handleMarkRead} />
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-ems-border px-4 py-2">
          <button
            onClick={onClose}
            className="w-full text-center text-xs text-ems-green hover:text-ems-green/80 transition-colors py-1"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
